import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext, useEffect, useState } from 'react';
import { useConfiguration } from '~/context/common/StaticContext';
import ModalContext from '~/context/vdp/ModalContext';
import ShowcaseContext from '~/context/vdp/ShowcaseContext';
import { getVehicleStockNumber } from '~/selectors';
import fetchSpinnerData from '~/services/vdp/fetchSpinnerData';
import useSelector from '~/store/useSelector';
import generateErrorLogger from '~/utilities/errorHandlers';

// Load Spinner data either (a) 15s after page load or (b) when the Key Features modal is opened
export default function useGetSpinnerData(loadAfter: number = 15000) {
  const { enableAutoShowcase } = useConfiguration();
  const { isKeyFeaturesModalOpen } = useContext(ModalContext);
  const { setActiveCategory, spinnerData } = useContext(ShowcaseContext);
  const stockNumber = useSelector(getVehicleStockNumber);
  const [shouldLoad, setShouldLoad] = useState(false);

  const { setData, setLoading, setError } = spinnerData;

  useEffect(() => {
    const t = setTimeout(() => {
      setShouldLoad(true);
    }, loadAfter);

    return () => {
      clearTimeout(t);
    };
  }, [loadAfter]);

  useEffect(() => {
    if (isKeyFeaturesModalOpen) {
      setShouldLoad(true);
    }
  }, [isKeyFeaturesModalOpen]);

  useEffectWithController(
    controller => {
      if (!shouldLoad || !enableAutoShowcase) return;

      setLoading();

      fetchSpinnerData(stockNumber, controller)
        .then(v => {
          setData(v);
        })
        .catch(
          generateErrorLogger('Get spinner data', undefined, {
            callback: setError
          })
        );
    },
    [
      enableAutoShowcase,
      setActiveCategory,
      setData,
      setError,
      setLoading,
      shouldLoad,
      stockNumber
    ]
  );
}
